import type { Dayjs } from '~/utils/dayjs';

import { dayjsTz, dayjs, convertDateToTz } from '~/utils/dayjs';
import { DATE_FORMAT } from '../constants/datePicker';
export function parseYearMonthDayDateString(input?: dayjs.ConfigType) {
  // Date-only strings (e.g. "1970-01-01") are treated as UTC, not local time
  // when using new Date()
  // We need to split year, month, day to pass into new Date() separately
  // to get a localized Date
  return dayjs(input).valueOf();
}
const VALID_YYYY_MM_DD_DATE_REGEX = /^\d{4}-\d{1,2}-\d{1,2}/;
export function isDate(date: number | string | Date) {
  return !isNaN(new Date(date).getDate());
}
export function isValidYearMonthDayDateString(date: string) {
  return VALID_YYYY_MM_DD_DATE_REGEX.test(date) && isDate(date);
}
export function isValidDate(date: string) {
  return date.length === 10 && isValidYearMonthDayDateString(date);
}

export function formatDate(
  timestamp: number | string,
  type?: 'YMD' | 'MY' | 'FULL' | 'FULL_WITH_TIME',
) {
  const date = convertDateToTz(timestamp);
  if (type === 'YMD') {
    return date.format(DATE_FORMAT.YMD);
  }
  if (type === 'MY') {
    return date.format(DATE_FORMAT.MONTH_YEAR);
  }
  if (type === 'FULL') {
    return date.format(DATE_FORMAT.FULL);
  }
  if (type === 'FULL_WITH_TIME') {
    return date.format(DATE_FORMAT.FULL_WITH_TIME);
  }
  return date.format(DATE_FORMAT.SHORT);
}

export function formatTime(timestamp: number | string) {
  const date = convertDateToTz(timestamp);
  return date.format(DATE_FORMAT.TIME);
}

export function isSameDayTimestamp(since: number | string, until: number | string) {
  const sinceDate = convertDateToTz(since);
  const untilDate = convertDateToTz(until);
  return sinceDate.isSame(untilDate, 'day');
}

export function formatTimeRange(data: {
  since: number | string;
  until: number | string;
  format?: string;
  showNow?: boolean;
}) {
  const { since, until, format = DATE_FORMAT.TIME, showNow = false } = data;
  const sinceDate = convertDateToTz(since);
  const untilDate = convertDateToTz(until);

  return `${sinceDate.format(format)} - ${showNow ? 'Now' : untilDate.format(format)}`;
}

export function formatDateTimeRange(data: {
  since: number | string;
  until: number | string;
  isShowNow?: boolean;
}) {
  const { since, until, isShowNow } = data;
  const sinceDate = convertDateToTz(since);
  const untilDate = convertDateToTz(until);
  const isToday = sinceDate.isSame(dayjsTz(), 'day');

  const startTime = sinceDate.format(DATE_FORMAT.TIME);
  const endTime = untilDate.format(DATE_FORMAT.TIME);

  if (isToday) {
    const formatEndTime = isShowNow ? 'Now' : endTime;
    return `Today at ${startTime} - ${formatEndTime}`;
  }

  if (isShowNow) {
    return `${sinceDate.format(DATE_FORMAT.SHORT_WITH_TIME)} - Now`;
  }

  if (sinceDate.isSame(untilDate, 'day')) {
    return `${sinceDate.format(DATE_FORMAT.SHORT_WITH_TIME)} - ${endTime}`;
  }

  if (sinceDate.isSame(untilDate, 'day')) {
    const time = formatTimeRange({ since, until });
    return `${sinceDate.format(DATE_FORMAT.SHORT)} (${time})`;
  }

  if (sinceDate.isSame(untilDate, 'year')) {
    return `${sinceDate.format(DATE_FORMAT.MONTH_DAY_WITH_TIME)} - ${untilDate.format(DATE_FORMAT.MONTH_DAY_WITH_TIME)}, ${untilDate.format(DATE_FORMAT.YEAR)}`;
  }

  return `${sinceDate.format(DATE_FORMAT.SHORT_WITH_TIME)} - ${untilDate.format(DATE_FORMAT.SHORT_WITH_TIME)}`;
}

export const formatDayjs = (date: Dayjs, isEndDay = false, formatTemplate?: string) => {
  // const isUTC = date.isUTC?.() ?? false;
  // if (isUTC) return date.format();

  if (isEndDay && isMidnight(date)) {
    return date.endOf('day').format(formatTemplate);
  }
  return date.format(formatTemplate);
};

export const getEndOfDayBy = (value?: string | Dayjs, formatTemplate?: string) => {
  if (!value) {
    return formatDayjs(dayjsTz().endOf('day'), false, formatTemplate);
  }

  return formatDayjs(dayjsTz(value).endOf('day'), false, formatTemplate);
};

export const isMidnight = (date: Dayjs) => {
  return date.hour() === 0 && date.minute() === 0;
};

export const formatMs = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds].map((v) => String(v).padStart(2, '0')).join(':');
};
